import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';

import ValidationMessage from './ValidationMessage';
import { Validation } from './validation-helper';
import 'react-phone-input-2/lib/material.css';

export interface Props {
  id: string;
  type: string;
  placeHolder: string;
  disabled?: boolean;
  onChange: (data: any) => void;
  clearFilters?: boolean;
  setCleared?: (cleared: boolean) => void;
  validation?: Validation;
  isRequired?: boolean;
  contactFormRole?: string;
  containerClassname?: string;
  preselectedValue?: string;
}

const InputField: React.FC<Props> = ({
  id,
  placeHolder,
  type,
  disabled,
  onChange,
  setCleared,
  clearFilters,
  isRequired,
  validation,
  containerClassname,
  contactFormRole,
  preselectedValue,
}) => {
  const errorClassName = validation && !validation.isValid ? 'error' : '';
  const [focused, setFocused] = React.useState(false);
  const [dataShrink, setShrink] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [hideCountryCode, setHideCountryCode] = useState(true);
  const [hideDefaultFlag, setHideDefaultFlag] = useState(true);
  const onFocus = () => {
    setFocused(true);
    setShrink(true);
  };

  const onBlur = () => {
    setFocused(false);
    inputValue.trim() ? setShrink(true) : setShrink(false);
  };

  const updateValue = (value) => {
    setInputValue(value);
    onChange(value);
  };

  const onFieldChange = (e: any) => {
    updateValue(e.target.value);
  };

  const onPhoneFocus = (e) => {
    const concernedElement = e.target.parentElement;

    setFocused(true);
    setShrink(true);
    setHideCountryCode(false);

    document.addEventListener('mousedown', (event) => {
      if (!concernedElement.contains(event.target)) {
        setFocused(false);

        if (e.target.value.trim()) {
          setShrink(true);
          setHideCountryCode(false);
          setHideDefaultFlag(false);
        } else {
          setShrink(false);
          setHideCountryCode(true);
        }
      }
    });
  };

  const onPhoneFieldChange = (value: string) => {
    setHideCountryCode(false);
    onChange(value);
    setHideDefaultFlag(false);
    setInputValue(value);
  };

  useEffect(() => {
    if (clearFilters) {
      setInputValue('');

      if (setCleared) {
        setCleared(false);
      }

      onChange('');
      setFocused(false);
      setShrink(false);
      setHideCountryCode(true);
    }
  }, [clearFilters]);

  const inputsRender = () => {
    switch (type) {
      case 'textarea':
        return (
          <textarea
            id={id}
            className="main-text-field-input"
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onFieldChange}
            contact-form-role={contactFormRole}
            value={inputValue}
            disabled={disabled}
            data-trigger="input-trigger-recaptcha"
          />
        );

      case 'tel':
        return (
          <PhoneInput
            inputClass="main-text-field-input"
            value={inputValue}
            containerClass={hideCountryCode ? 'hide-country-code' : ''}
            buttonClass={hideDefaultFlag ? 'hide-default-flag' : ''}
            disabled={disabled}
            placeholder=""
            specialLabel=""
            onChange={onPhoneFieldChange}
            inputProps={{
              name: 'phone',
              id,
              onFocus: onPhoneFocus,
              'contact-form-role': contactFormRole,
              'aria-invalid': 'false',
              'data-trigger': 'input-trigger-recaptcha',
            }}
          />
        );

      default:
        return (
          <input
            type={type}
            id={id}
            className="main-text-field-input"
            aria-invalid="false"
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onFieldChange}
            contact-form-role={contactFormRole}
            value={inputValue}
            disabled={disabled}
            data-trigger="input-trigger-recaptcha"
          />
        );
    }
  };

  const labelAutoFill = inputValue.length > 0;

  useEffect(() => {
    if (!inputValue && preselectedValue) {
      updateValue(preselectedValue);
    }
  }, [preselectedValue]);

  return (
    <>
      <div className={`main-text-field ${containerClassname} ${disabled ? 'disabled' : ''} ${errorClassName}`}>
        <label
          className={`main-text-field-label ${focused ? 'focused' : ''} ${
            dataShrink || labelAutoFill ? 'shrink' : ''
          } ${errorClassName}`}
          htmlFor={type}
          id={`${type}-label`}
        >
          {placeHolder}
        </label>
        <div className={`text-input-field ${focused ? 'focused' : ''} ${errorClassName}`}>
          {inputsRender()}
          <fieldset aria-hidden="true" className="main-text-field-fieldset">
            <legend className="main-text-field-legend">
              <span>{placeHolder}</span>
            </legend>
          </fieldset>
        </div>
      </div>
      {isRequired && validation && !validation.isValid && <ValidationMessage text={validation.validationMessage} />}
    </>
  );
};

export default InputField;
